<template>
  <div class="columns is-multiline is-marginless">
    <div class="column is-12">
      <h3 class="is-size-3 has-text-centered has-margin-bottom-150">
        {{ loading ? "Loading..." : step.label }}
      </h3>

      <step-tracker
        :total="enabledSteps.length"
        :active="stepNum"
        class="has-margin-bottom-200"
        @click="stepTo(canStepBack && $event < stepNum ? $event : stepNum)"
      />

      <loading v-if="loading" />

      <component
        :is="step.component"
        v-if="!loading"
        :payload="payload"
        @payload="onPayload"
        @next="stepTo()"
        @back="stepTo(stepNum - 1)"
        @reset="reset()"
        @startOver="startOver()"
      />
    </div>
  </div>
</template>

<script>
import currencies from "@src/data/currencies";
export default {
  name: "GoogleCleanup",
  data() {
    return {
      loading: false,
      stepNum: null,
      steps: [
        {
          label: "Google × fixed.net",
          component: () => import("./_introStep"),
          beforeEnter: () => Promise.resolve(),
          isEnabled: () => {
            return true;
          }
        },
        {
          label: "Create account",
          component: () => import("./_createAccountStep"),
          beforeEnter: () => {
            return new Promise((resolve, reject) => {
              if (!this.payload.couponId) return reject();
              if (!this.payload.siteUrl) return reject();
              resolve();
            });
          },
          isEnabled: () => {
            return true;
          }
        },
        {
          label: "Setting things up",
          component: () => import("./_provisioningStep"),
          beforeEnter: () => {
            return new Promise((resolve, reject) => {
              if (!this.payload.couponId) return reject();
              if (!this.payload.siteUrl) return reject();
              if (!this.payload.userId) return reject();
              resolve();
            });
          },
          isEnabled: () => {
            return true;
          }
        },
        {
          label: "Welcome aboard",
          component: () => import("./_confirmationStep"),
          beforeEnter: () => {
            return new Promise((resolve, reject) => {
              if (!this.payload.siteId) return reject();
              // The confirmation copy reads straight off siteUrl
              if (!this.payload.siteUrl) return reject();
              resolve();
            });
          },
          isEnabled: () => {
            return true;
          }
        }
      ],
      payload: {}
    };
  },
  computed: {
    enabledSteps() {
      return this.$_.filter(this.steps, step => {
        return step.isEnabled();
      });
    },
    step() {
      return this.enabledSteps[this.stepNum];
    },
    /* Once provisioning has started (or finished) the earlier steps no longer
    make sense to revisit. */
    canStepBack() {
      return this.stepNum < 2;
    }
  },
  watch: {
    "step.component"() {
      this.loading = true;
      this.step.component().then(() => {
        this.step
          .beforeEnter()
          .then(() => {
            this.loading = false;
          })
          .catch(() => {
            this.loading = false;
            this.stepTo(this.stepNum - 1);
          });
      });
    },
    "$route.query.step"() {
      this.$set(this, "stepNum", parseInt(this.$route.query.step || 0));
    },
    payload: {
      handler(newPayload, oldPayload) {
        if (this.$_.isEmpty(oldPayload)) return;
        if (newPayload.taskId)
          return sessionStorage.removeItem("googleCleanup");
        try {
          sessionStorage.setItem(
            "googleCleanup",
            JSON.stringify(this.$_.omit(newPayload, ["userId"]))
          );
        } catch {
          // Fail silently (not critical)
        }
      },
      deep: true
    }
  },
  created() {
    this.reset();
    this.stepTo(parseInt(this.$route.query.step) || 0); // Set initial step
  },

  methods: {
    onPayload(event) {
      Object.keys(event).forEach(key => {
        this.payload[key] = event[key];
      });
    },
    /* Discard all progress and return to the first step – used when
    provisioning hits an unrecoverable error (eg. coupon already redeemed). */
    startOver() {
      sessionStorage.removeItem("googleCleanup");
      this.reset();
      this.stepTo(0);
    },
    stepTo(stepNum = this.stepNum + 1) {
      if (this.loading) return;
      if (stepNum <= this.enabledSteps.length - 1) {
        this.stepNum = stepNum;
        this.$router.push({
          query: this.$_.merge({}, this.$route.query, { step: stepNum })
        });
        this.$gtm.trackEvent({
          event: "app.event",
          category: "Google Cleanup Onboarding",
          action: "Step to",
          label: this.step.label
        });
      } else {
        this.stepTo(0);
      }
    },
    reset() {
      let progress;
      try {
        progress = JSON.parse(sessionStorage.getItem("googleCleanup")) || {};
      } catch {
        // No progress or error parsing malformed sessionStorage value
        sessionStorage.removeItem("googleCleanup");
        progress = {};
      }

      const queryCoupon = this.$_.get(this.$route.query, "c", "");

      /* The query params only seed the flow – anything the user has since
      entered/confirmed (stored as progress) takes precedence. A different
      referral link, however, supersedes stored progress entirely. */
      if (queryCoupon && progress.queryCoupon !== queryCoupon) progress = {};

      let currency = this.$_.get(this.$route.query, "currency");
      currency = this.$_.keys(currencies).includes(currency) ? currency : null;
      this.$set(
        this,
        "payload",
        this.$_.merge(
          {
            couponId: queryCoupon,
            currency,
            orderNum: null,
            promotionDescription: "",
            queryCoupon,
            sitePrefill: this.$_.get(this.$route.query, "site", ""),
            siteId: null,
            siteSoftware: "wordpress",
            siteUrl: null,
            taskId: null,
            userId: this.$store.getters["auth/userId"]() || null
          },
          progress
        )
      );
    }
  }
};
</script>
